/**
 * A GANGORRA DA MÉDIA — caixas iguais sobre uma prancha graduada, e um apoio por baixo.
 *
 * Uma prancha sem peso, com caixas iguais apoiadas nela, fica em equilíbrio quando o
 * apoio está na MÉDIA das posições das caixas: é o centro de massa. A figura torna
 * visível o que a conta esconde — cada caixa "puxa" com a distância até o apoio, e uma
 * caixa isolada lá na ponta pesa tanto quanto várias perto do meio.
 *
 * A PRANCHA NÃO INCLINA. Dois calços nas pontas a mantêm presa enquanto o aluno move o
 * apoio: se ela balançasse a cada posição, bastaria procurar o ponto em que ela fica
 * reta, e o item mediria paciência em vez de média.
 *
 * Números só nas pontas, 0 e o máximo; as marcas de cada inteiro ficam, para contar.
 */

import { txt } from '../schema/richtext.ts';
import { scene, type Scene, type Shape } from './scene.ts';

/** Unidades de desenho por unidade da prancha. */
export const U_GANGORRA = 40;

export interface OpcoesGangorra {
  /** Uma posição por caixa; posições repetidas empilham. */
  readonly posicoes: readonly number[];
  readonly apoio: number;
  readonly max?: number;
  readonly title: string;
  readonly desc: string;
}

export function gangorra(o: OpcoesGangorra): Scene {
  const U = U_GANGORRA;
  const max = o.max ?? 10;
  const E = 0.35 * U; // a prancha passa um pouco do 0 e do máximo
  const T = 0.22 * U; // espessura da prancha
  const S = 0.72 * U; // lado da caixa
  const ALT = 1.2 * U; // altura do apoio
  const L = max * U;

  const shapes: Shape[] = [];
  shapes.push({
    t: 'seg',
    a: { x: -E - 0.6 * U, y: -ALT },
    b: { x: L + E + 0.6 * U, y: -ALT },
    role: 'auxiliary',
  });
  // Os calços que prendem a prancha.
  shapes.push({
    t: 'rect',
    at: { x: -E, y: -ALT, w: 0.28 * U, h: ALT },
    role: 'muted',
    fill: 'muted-fill',
  });
  shapes.push({
    t: 'rect',
    at: { x: L + E - 0.28 * U, y: -ALT, w: 0.28 * U, h: ALT },
    role: 'muted',
    fill: 'muted-fill',
  });
  // O apoio, com a ponta encostada na prancha.
  const f = o.apoio * U;
  shapes.push({
    t: 'poly',
    pts: [
      { x: f, y: 0 },
      { x: f - 0.42 * U, y: -ALT },
      { x: f + 0.42 * U, y: -ALT },
    ],
    closed: true,
    role: 'result',
    fill: 'result-fill',
  });
  shapes.push({
    t: 'rect',
    at: { x: -E, y: 0, w: L + 2 * E, h: T },
    role: 'given',
    fill: 'surface',
  });
  for (let i = 0; i <= max; i++) {
    shapes.push({
      t: 'seg',
      a: { x: i * U, y: 0 },
      b: { x: i * U, y: T * 0.6 },
      role: 'auxiliary',
    });
  }
  shapes.push({
    t: 'label',
    at: { x: -E, y: T / 2 },
    text: [txt('0')],
    anchor: 'w',
    emphasis: 'tick',
  });
  shapes.push({
    t: 'label',
    at: { x: L + E, y: T / 2 },
    text: [txt(String(max))],
    anchor: 'e',
    emphasis: 'tick',
  });

  const pilhas = new Map<number, number>();
  for (const p of o.posicoes) {
    const j = pilhas.get(p) ?? 0;
    pilhas.set(p, j + 1);
    shapes.push({
      t: 'rect',
      at: { x: p * U - S / 2, y: T + j * S, w: S, h: S },
      role: 'given',
      fill: 'given-fill',
    });
  }
  const maior = Math.max(1, ...pilhas.values());

  // Enquadramento fixo: não pode depender do apoio, senão a figura pula ao mover.
  return scene(shapes, {
    viewBox: {
      x: -E - 1.3 * U,
      y: -ALT - 0.3 * U,
      w: L + 2 * E + 2.6 * U,
      h: ALT + 0.3 * U + T + maior * S + 0.5 * U,
    },
    a11y: { title: o.title, desc: o.desc },
  });
}
